import { useState } from "react";
import { toast } from "sonner";
import { Printer, Mail, PiggyBank } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useApi, useLoad } from "@/lib/api";
import { gbp, TYPES } from "@/lib/format";
import { PageHead, Empty } from "@/components/Bits";

const lastMonth = () => { const d = new Date(); d.setDate(0); return d.toLocaleDateString("en-CA").slice(0, 7); };
const monthName = (m) => new Date(`${m}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });

const Big = ({ label, value, tone = "text-slate-900", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-4 print:border-slate-400">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">{label}</p>
    <p className={`mt-1 font-display text-2xl font-extrabold ${tone}`} data-testid={testid}>{value}</p>
  </div>
);

export default function SummaryPage() {
  const api = useApi();
  const [month, setMonth] = useState(lastMonth());
  const { data: s, loading, error } = useLoad(`/summary?month=${month}`);
  const [busy, setBusy] = useState(false);
  const email = async () => {
    setBusy(true);
    try { await api("post", "/summary/email", { month }); toast.success("Summary emailed to you"); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  const t = s?.totals;
  return (
    <div data-testid="summary-page">
      <div className="print:hidden">
        <PageHead title="Month-end summary" sub="Money recovered per wholesaler. Owners get this by email on the 1st of each month.">
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)} className="w-44" data-testid="summary-month-input" />
          <Button variant="outline" onClick={() => window.print()} data-testid="summary-print-button"><Printer className="mr-1 h-4 w-4" /> Print</Button>
          <Button onClick={email} disabled={busy} data-testid="summary-email-button"><Mail className="mr-1 h-4 w-4" /> Email to me</Button>
        </PageHead>
      </div>
      <div className="hidden print:block print:mb-4">
        <p className="text-2xl font-extrabold">InvoiceReconciler.app</p>
        <p className="text-lg">Month-end wholesaler summary · {monthName(month)}</p>
      </div>
      {loading && !s && <p className="text-sm text-slate-400" data-testid="summary-loading">Loading…</p>}
      {error && <p className="text-sm text-rose-700" data-testid="summary-error">Could not load the summary: {error}</p>}
      {s && (
        <div className="space-y-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <Big label={`Recovered in ${monthName(month)}`} value={gbp(t.recovered)} tone="text-emerald-700" testid="summary-total-recovered" />
            <Big label="Queries resolved" value={t.resolved} testid="summary-total-resolved" />
            <Big label="Queries raised" value={t.raised} testid="summary-total-raised" />
            <Big label="Still owed back now" value={gbp(t.still_owed)} tone={t.still_owed ? "text-rose-600" : "text-slate-900"} testid="summary-total-owed" />
          </div>
          {s.suppliers.length === 0 ? <Empty icon={PiggyBank} title="No query activity" text={`Nothing raised, resolved or open for ${monthName(month)}.`} testid="summary-empty" /> : (
            <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
              <table className="w-full text-sm" data-testid="summary-table">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr><th className="p-3">Wholesaler</th><th className="p-3 text-right">Recovered</th><th className="p-3 text-right">Resolved</th>
                    <th className="p-3 text-right">Raised</th><th className="p-3 text-right">Open now</th><th className="p-3 text-right">Still owed</th></tr>
                </thead>
                <tbody>
                  {s.suppliers.map((c) => (
                    <tr key={c.supplier} className="border-t border-slate-100" data-testid={`summary-row-${c.supplier.toLowerCase().replace(/\s+/g, "-")}`}>
                      <td className="p-3 font-semibold">{c.supplier}</td>
                      <td className="p-3 text-right font-mono font-semibold text-emerald-700">{gbp(c.recovered)}</td>
                      <td className="p-3 text-right">{c.resolved}</td><td className="p-3 text-right">{c.raised}</td><td className="p-3 text-right">{c.open}</td>
                      <td className="p-3 text-right font-mono">{gbp(c.still_owed)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {s.resolved.length > 0 && (
            <div>
              <h2 className="mb-3 text-lg font-bold">Resolved in {monthName(month)}</h2>
              <ul className="divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white" data-testid="summary-resolved-list">
                {s.resolved.map((r) => (
                  <li key={r.thread} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 p-3 text-sm">
                    <span className="font-mono text-xs text-slate-500">{r.thread}</span>
                    <span className="font-semibold">{r.supplier}</span>
                    <span className="text-slate-500">{TYPES[r.type] || r.type} · {r.ref}</span>
                    <span className="ml-auto font-mono font-semibold text-emerald-700">{gbp(r.at_stake)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
